import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  TimeScale,
  Legend,
  Title,
} from "chart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { getDaySearchCount } from "../../Service/apiService";
import "../../assets/css/Analytics.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  TimeScale,
  Legend,
  Title
);

// Helper: get date range (ISO strings)
const getDateRange = (range) => {
  const now = new Date();
  let start = new Date(now);

  switch (range) {
    case "7d":
      start.setDate(now.getDate() - 7);
      break;
    case "1m":
      start.setMonth(now.getMonth() - 1);
      break;
    case "3m":
      start.setMonth(now.getMonth() - 3);
      break;
    case "6m":
      start.setMonth(now.getMonth() - 6);
      break;
    case "1y":
    default:
      start.setFullYear(now.getFullYear() - 1);
  }

  
  return {
    start: start.toISOString().split("T")[0],
    end: now.toISOString().split("T")[0],
  };
};

export default function UserActivityAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();

  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");

  // Fetch & process data
  useEffect(() => {
    const fetchDailySearchCount = async () => {
      setLoading(true);
      try {
        // your API ignores startDate/endDate → fetch all, then filter client-side
        const res = await getDaySearchCount({}); // empty body or omit if API doesn't use it

        // Ensure array
        const rawData = Array.isArray(res) ? res : [];

        // Sort ascending: oldest → newest (REQUIRED for TimeScale)
        const sortedData = [...rawData]
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map((item) => ({
            date: new Date(item.date).toISOString().split("T")[0], // "2025-12-24"
            count: item.count || 0,
          }));

        // 2️ Filter by selected time range
        const { start: rangeStartStr, end: rangeEndStr } =
          getDateRange(timeRange);
        const rangeStart = new Date(rangeStartStr);
        const rangeEnd = new Date(rangeEndStr);

        const filtered = sortedData.filter((item) => {
          const d = new Date(item.date);
          return d >= rangeStart && d <= rangeEnd;
        });

        setDailyData(filtered);
      } catch (err) {
        console.error("Failed to fetch daily search count:", err);
        setDailyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDailySearchCount();
  }, [timeRange]);

  // Chart data
  const chartData = useMemo(() => {
    return {
      datasets: [
        {
          label: "Daily Search Count",
          data: dailyData.map((d) => ({ x: d.date, y: d.count })),
          borderColor: "#194a7a",
          backgroundColor: "rgba(25, 74, 122, 0.08)",
          tension: 0.3,
          fill: true,
          borderWidth: 2.5,
          pointRadius: dailyData.length <= 14 ? 3 : 0,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [dailyData]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "top" },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#fff",
        titleColor: "#194a7a",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
        callbacks: {
          title: (tooltipItems) => {
            const date = new Date(tooltipItems[0].parsed.x);
            return date.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });
          },
          label: (ctx) => `Searches: ${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit:
            timeRange === "1y"
              ? "month"
              : timeRange === "6m" || timeRange === "3m"
              ? "week"
              : "day",
          tooltipFormat: "MMM d, yyyy",
          displayFormats: {
            day: "MMM d",
            week: "MMM d",
            month: "MMM yyyy",
          },
        },
        title: {
          display: true,
          text: "Date",
          font: { size: 12, weight: "bold" },
        },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: timeRange === "1y" ? 12 : 14,
        },
      },
      y: {
        title: {
          display: true,
          text: "Search Count",
          font: { size: 12, weight: "bold" },
        },
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          precision: 0,
          callback: (v) => v.toLocaleString(),
        },
      },
    },
  };

  const handleBack = () => {
    const currentPath = location.pathname;
    const dashboardPath = currentPath.replace("/analytics", "/dashboard");
    navigate(dashboardPath);
  };

  const timeOptions = [
    { label: "Last 7 Days", value: "7d" },
    { label: "Last 1 Month", value: "1m" },
    { label: "Last 3 Months", value: "3m" },
    { label: "Last 6 Months", value: "6m" },
    { label: "Last 1 Year", value: "1y" },
  ];

  const UserActivitySkeleton = () => (
  <div className="chart-wrapper" style={{ height: "420px" }}>
    {/* Chart title skeleton */}
    <Skeleton
      height={20}
      width={260}
      style={{ marginBottom: 16 }}
    />

    {/* Chart area skeleton */}
    <Skeleton
      height={360}
      borderRadius={12}
    />
  </div>
);


  return (
    <div className="analytics-container">
      <div className="header-section">
        <h1 className="title">Daily Search Count</h1>
        <div className="time-filter">
          <label style={{ marginRight: "8px", fontSize: "14px" }}></label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              fontSize: "14px",
              padding: "4px 8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          >
            {timeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="analytics-content">
        <div className="chart-panel">
          <h3 style={{ marginBottom: "12px" }}>
            Search Activity —{" "}
            {timeOptions.find((o) => o.value === timeRange)?.label}
          </h3>

          {loading ? (
             <UserActivitySkeleton />
          ) : dailyData.length > 0 ? (
            <div className="chart-wrapper" style={{ height: "420px" }}>
              <Line data={chartData} options={chartOptions} />
            </div>
          ) : (
            <p
              style={{ textAlign: "center", color: "#888", padding: "40px 0" }}
            >
              No search data for this period.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
